import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import useMerchandiseSearchTypeahead from 'hooks/Merchandise/useMerchandiseSearchTypeahead'
import useDebouncedCallback from 'hooks/useDebouncedCallback'
import useToggle from 'hooks/useToggle'
import React, { useCallback, useRef, useState } from 'react'
import MerchandiseSearchFormPlaceholderResults from './MerchandiseSearchFormPlaceholderResults'
import MerchandiseSearchFormResults from './MerchandiseSearchFormResults'
import MerchandiseSearchFormNoResults from './MerchandiseSearchFormNoResults'

interface Props {
  defaultText?: string;
  /**
   * Optionally provide a ref that will anchor the dropdown to
   * Otherwise, it will be the size of the input element
   */
  anchorRef?: React.RefObject<HTMLElement | null>;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
}

function MerchandiseSearchDropdownInput(props: Props) {
  const { defaultText, anchorRef, onClick } = props
  const [open, _, setOpen, setClosed] = useToggle(false)
  const triggerRef = useRef<HTMLInputElement>(null)
  const [text, setText] = useState(defaultText ?? '')

  const debounceChange = useDebouncedCallback((text: string) => {
    setText(text)
  }, 500, [])

  const onSearchChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    debounceChange(e.target.value)
  }, [debounceChange])

  const [results, loading] = useMerchandiseSearchTypeahead(text)

  const onClear = useCallback(() => {
    setText('')
  }, [])

  const onInputFocus = useCallback(() => {
    setOpen()
  }, [setOpen])

  const hasResults = !!results.products.length || !!results.categories.length || !!results.brands.length

  return <>
    <SearchFormTextField
      placeholder="Search products, categories or brands"
      ref={triggerRef}
      onFocus={onInputFocus}
      onChange={onSearchChange}
      onClick={onClick}
      onClear={onClear}
      defaultValue={text}
      displayValue={text}
      autoComplete="off"
      name="search"
    />
    <DropdownSheet
      size="fill-anchor"
      anchorRef={anchorRef ?? triggerRef}
      triggerRef={triggerRef}
      onClose={setClosed}
      open={open}
    >
      {!text.length && <MerchandiseSearchFormPlaceholderResults />}
      {!!text.length && <>
        {!loading && !hasResults && <MerchandiseSearchFormNoResults text={text} />}
        {(loading || hasResults) && <MerchandiseSearchFormResults
          results={results}
          text={text}
          loading={loading}
        />}
      </>}
    </DropdownSheet>
  </>
}

export default MerchandiseSearchDropdownInput
